import React, { useState } from 'react';
import { Trash2, Plus, MessageCircleMore, MessageSquareQuote, Search, ChevronDown, Braces } from 'lucide-react';
import { Drawer, DrawerContent, DrawerHeader, DrawerOverlay } from 'equal-ds-ui';
import DOMPurify from 'dompurify';
import type { CreateNudgeFormData } from '../../../services/nudges-service';
import type { NudgeFormErrors, NotificationType } from '../../../types/nudges';
import { analyticsService, type TableRow, type TableColumn } from '../../../services/analytics-service';
import { AnalyticsPortalTable } from './AnalyticsPortalTable';
import { validateSQLQuery } from '../../../utils/validators';
import usersListSvg from '../../../assets/images/usersList.svg';
import './analytics-portal.css';

/**
 * Sanitizes user input using DOMPurify to prevent XSS attacks
 * Strips all HTML tags for plain text fields
 */
const sanitizeInput = (input: string): string => {
  if (!input) return '';

  return DOMPurify.sanitize(input, {
    ALLOWED_TAGS: [],
    ALLOWED_ATTR: [],
    KEEP_CONTENT: true,
  });
};

interface NudgeDrawerProps {
    onClose: () => void;

    // Form State
    nudgeForm: CreateNudgeFormData;
    setNudgeForm: (form: CreateNudgeFormData) => void;
    formErrors: NudgeFormErrors;
    setFormErrors: (errors: NudgeFormErrors) => void;

    // Actions
    onCreateNudge: () => Promise<void>;
    isCreating: boolean;

    // JSON State for User Targets
    jsonTextState: Record<number, string>;
    setJsonTextState: React.Dispatch<React.SetStateAction<Record<number, string>>>;
    jsonValidationErrors: Record<number, string>;
    setJsonValidationErrors: React.Dispatch<React.SetStateAction<Record<number, string>>>;

    // Helper Functions
    addUserTarget: () => void;
    updateUserTarget: (index: number, field: 'user_id' | 'user_placeholders', value: string | Record<string, string>) => void;
    removeUserTarget: (index: number) => void;
    getJsonText: (index: number) => string;
    validateAndUpdateJson: (index: number, text: string) => void;

    // Query Search State
    queryResults: TableRow[];
    setQueryResults: (results: TableRow[]) => void;
    queryColumns: TableColumn[];
    setQueryColumns: (columns: TableColumn[]) => void;
    isSearching: boolean;
    setIsSearching: (loading: boolean) => void;
    queryError: string;
    setQueryError: (error: string) => void;
}

export const NudgeDrawer: React.FC<NudgeDrawerProps> = ({
    nudgeForm,
    setNudgeForm,
    formErrors,
    onCreateNudge,
    isCreating,
    //  jsonTextState,
    jsonValidationErrors,
    addUserTarget,
    updateUserTarget,
    removeUserTarget,
    getJsonText,
    validateAndUpdateJson,
    queryResults,
    setQueryResults,
    queryColumns,
    setQueryColumns,
    isSearching,
    setIsSearching,
    queryError,
    setQueryError
}) => {
    // Local state for query search
    const [queryInput, setQueryInput] = useState<string>('');
    const [chatId] = useState<string>(`chat-nudge-${Date.now()}`);
    // queryMode is always TEXTUAL (SQL toggle removed)

    // Pagination state
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(10);
    const [totalRows, setTotalRows] = useState<number>(0);

    // Handle query search
    const handleQuerySearch = async (pageOverride?: number, pageSizeOverride?: number) => {
        if (!queryInput.trim()) {
            setQueryError('Please enter a query');
            return;
        }

        // Validate SQL query for security (prevent SQL injection)
        const sqlValidationError = validateSQLQuery(queryInput);
        if (sqlValidationError) {
            setQueryError(sqlValidationError);
            return;
        }

        setQueryError('');
        setIsSearching(true);

        // Define max safe result size
        const MAX_SAFE_RESULTS = 10000;

        // Use override if provided, otherwise use current page state
        // NOTE: For new searches or page size changes, pass 1 as override
        const pageToFetch = pageOverride !== undefined ? pageOverride : currentPage;
        const sizeToUse = pageSizeOverride !== undefined ? pageSizeOverride : pageSize;

        try {
            const response = await analyticsService.querySearch(
                queryInput,
                chatId,
                pageToFetch,
                sizeToUse
            );

            // Extract results with null safety
            const { query, columns, rows, totalRows: total } = response.data;

            // Handle empty results
            if (!rows || rows.length === 0) {
                setQueryError('No users found matching your query');
                setQueryResults([]);
                setQueryColumns(columns || []);
                setTotalRows(0);
                return;
            }

            // Warn if result set is very large
            if (total && total > MAX_SAFE_RESULTS) {
                setQueryError(`Warning: Query returned ${total.toLocaleString()} users. This may affect performance. Consider refining your query.`);
            }

            // Update state with fallbacks
            setQueryResults(rows);
            setQueryColumns(columns || []);
            setTotalRows(total || 0);

            // Update current page if we used an override
            if (pageOverride !== undefined) {
                setCurrentPage(pageOverride);
            }

            // Also update the sql_query field in the form (fallback to empty string if undefined)
            setNudgeForm({ ...nudgeForm, sql_query: query || '' });

        } catch (error) {
            console.error('Query search error:', error);
            setQueryError('Failed to execute query. Please try again.');
        } finally {
            setIsSearching(false);
        }
    };

    return (
        <>
            <DrawerOverlay />
            <Drawer variant="overlay" width="75%">
                <DrawerContent className="equal-ds-root w-full flex flex-col flex-1 h-full bg-white">
                    <DrawerHeader className="border-b border-gray-200 px-4 py-4 bg-white">
                        <div className="flex items-center gap-3">
                            <div className="bg-[#E6F3FB] p-1.5 rounded-md">
                                <MessageSquareQuote className="w-5 h-5 text-[#0B334A]" />
                            </div>
                            <div>
                                <h2 className="text-xl font-bold text-gray-900">
                                    CREATE NUDGE
                                </h2>
                                <p className="text-sm text-gray-700 mt-1">
                                    Target user personas and create customised nudges for them
                                </p>
                            </div>
                        </div>
                    </DrawerHeader>

                    <div className="flex-1 overflow-y-auto p-4 space-y-8">
                        {/* Section 1: Targeting Method Selection */}
                        <section>
                            <h3 className="text-sm font-semibold text-gray-700 mb-3">
                                1. Choose how you would like to select users
                            </h3>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {/* Query Based */}
                                <div
                                    className={`cursor-pointer rounded-xl p-4 flex gap-3 transition bg-white ${nudgeForm.targeting_mode === "QUERY_BASED" ? "" : "hover:border-gray-300"
                                        }`}
                                    style={{
                                        border: nudgeForm.targeting_mode === "QUERY_BASED" ? "2px solid #72BFE6" : "1px solid #808080",
                                        boxShadow: nudgeForm.targeting_mode === "QUERY_BASED" ? "0 0 0 1px #72BFE6" : "none"
                                    }}
                                    onClick={() => setNudgeForm({ ...nudgeForm, targeting_mode: "QUERY_BASED" })}
                                >
                                    <MessageCircleMore className="w-6 h-6 mt-1 text-[#0B334A]" />
                                    <div>
                                        <h4 className="font-semibold text-[#0B334A]">Query-Based</h4>
                                        <p className="text-xs text-gray-600 mt-1">
                                            Use natural language/SQL query to select your users
                                        </p>
                                    </div>
                                </div>

                                {/* Manual Targeting */}
                                <div
                                    className={`cursor-pointer rounded-xl p-4 flex gap-3 transition bg-white ${nudgeForm.targeting_mode === "DIRECT_USER_IDS" ? "" : "hover:border-gray-300"
                                        }`}
                                    style={{
                                        border: nudgeForm.targeting_mode === "DIRECT_USER_IDS" ? "2px solid #72BFE6" : "1px solid #808080",
                                        boxShadow: nudgeForm.targeting_mode === "DIRECT_USER_IDS" ? "0 0 0 1px #72BFE6" : "none"
                                    }}
                                    onClick={() => setNudgeForm({ ...nudgeForm, targeting_mode: "DIRECT_USER_IDS" })}
                                >
                                    <Braces className="w-6 h-6 mt-1 text-[#0B334A]" />
                                    <div>
                                        <h4 className="font-semibold text-[#0B334A]">Manual Targeting</h4>
                                        <p className="text-xs text-gray-600 mt-1">
                                            Individually specify the users you want to select
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </section>
                        {/* Section 2: Targeting Configuration */}
                        <section className="space-y-4">
                            {nudgeForm.targeting_mode === 'QUERY_BASED' ? (
                                <>
                                    <div className="flex gap-2">
                                        <div className="flex flex-1 rounded-lg focus-within:ring-2 focus-within:ring-blue-500 nudge-input-bordered" style={{ overflow: 'hidden' }}>
                                            <div className="flex items-center justify-center px-3 bg-white">
                                                <Search className="h-4 w-4 text-gray-400" />
                                            </div>
                                            <div className="flex-1">
                                                <input
                                                    type="text"
                                                    value={queryInput}
                                                    onChange={(e) => setQueryInput(e.target.value)}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter' && !isSearching) {
                                                            handleQuerySearch(1);
                                                        }
                                                    }}
                                                    placeholder="Describe the users you want to target"
                                                    className="w-full px-4 py-3 focus:outline-none text-sm placeholder:text-gray-600 nudge-input-no-border"
                                                    disabled={isSearching}
                                                />
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => !isSearching && handleQuerySearch(1)}
                                            disabled={isSearching}
                                            className={`nudge-action-btn px-4 flex items-center rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                                                isSearching
                                                    ? 'cursor-not-allowed border border-gray-300'
                                                    : 'cursor-pointer'
                                            }`}
                                        >
                                            {isSearching ? 'Searching...' : 'Search'}
                                        </button>
                                    </div>
                                    {queryError && (
                                        <p className="text-red-600 text-xs mt-2">{queryError}</p>
                                    )}
                                    {formErrors.sql_query && <p className="text-red-600 text-xs mt-1">{formErrors.sql_query}</p>}

                                    {isSearching ? (
                                        // Skeleton loading state - show whenever a query is being searched
                                        <div className="rounded-lg overflow-hidden bg-white nudge-input-bordered">
                                            <div className="p-4 border-b border-gray-200">
                                                <div className="h-5 bg-gray-200 rounded w-32 animate-pulse"></div>
                                            </div>
                                            <div className="p-4">
                                                {/* Skeleton table header */}
                                                <div className="flex gap-4 mb-3 pb-3 border-b border-gray-200">
                                                    {[1, 2, 3, 4].map((i) => (
                                                        <div key={i} className="flex-1 h-4 bg-gray-200 rounded animate-pulse"></div>
                                                    ))}
                                                </div>
                                                {/* Skeleton table rows */}
                                                {[1, 2, 3, 4, 5].map((row) => (
                                                    <div key={row} className="flex gap-4 mb-3">
                                                        {[1, 2, 3, 4].map((col) => (
                                                            <div key={col} className="flex-1 h-4 bg-gray-100 rounded animate-pulse"></div>
                                                        ))}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ) : queryResults.length > 0 ? (
                                        <div className="rounded-lg overflow-visible relative nudge-input-bordered">
                                            <AnalyticsPortalTable
                                                title="Query Results"
                                                columns={queryColumns as TableColumn[]}
                                                data={queryResults as TableRow[]}
                                                currentPage={currentPage}
                                                pageSize={pageSize}
                                                totalItems={totalRows}
                                                onPageChange={(page) => handleQuerySearch(page)}
                                                onPageSizeChange={(size) => {
                                                    setPageSize(size);
                                                    setCurrentPage(1);
                                                    handleQuerySearch(1, size);
                                                }}
                                                isLoading={isSearching}
                                                dropdownZIndex={1400}
                                            />
                                        </div>
                                    ) : queryError === 'No users found matching your query' ? (
                                        // Show table with "No users found" message when search returns no results
                                        <div className="rounded-lg overflow-visible relative nudge-input-bordered">
                                            <AnalyticsPortalTable
                                                title="Query Results"
                                                columns={queryColumns.length > 0 ? queryColumns as TableColumn[] : [{ key: 'result', label: 'Results' }]}
                                                data={[]}
                                                currentPage={1}
                                                pageSize={pageSize}
                                                totalItems={0}
                                                onPageChange={(page) => handleQuerySearch(page)}
                                                onPageSizeChange={(size) => {
                                                    setPageSize(size);
                                                    setCurrentPage(1);
                                                    handleQuerySearch(1, size);
                                                }}
                                                isLoading={false}
                                                dropdownZIndex={1400}
                                            />
                                        </div>
                                    ) : (
                                        <div className="bg-gray-50 rounded-lg p-8 flex flex-col items-center justify-center text-center" style={{ border: '1.5px dashed #6B7280' }}>
                                            <div className="w-24 h-24 flex items-center justify-center mb-4">
                                                <img src={usersListSvg} alt="Empty state" className="w-full h-full object-contain" />
                                            </div>
                                            <p className="text-gray-700 text-sm font-medium">Results</p>
                                            <p className="text-gray-400 text-xs mt-1">Enter a query to see list of users and available placeholders</p>
                                        </div>
                                    )}
                                </>
                            ) : (
                                <div className="space-y-3">
                                    <div className="flex justify-between items-center">
                                        <label className="text-sm font-semibold text-gray-700">User Targets</label>
                                        <button
                                            type="button"
                                            onClick={addUserTarget}
                                            className="text-xs flex items-center gap-1 text-blue-600 hover:text-blue-700 font-medium"
                                        >
                                            <Plus className="h-3 w-3" />
                                            Add Target
                                        </button>
                                    </div>

                                    {nudgeForm.user_targets?.map((target, index) => (
                                        <div key={index} className="border border-gray-700 rounded-lg p-4 bg-gray-50 relative group">
                                            <div className="flex items-center justify-between mb-3">
                                                <span className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Target {index + 1}</span>
                                                {(nudgeForm.user_targets?.length || 0) > 1 && (
                                                    <button
                                                        type="button"
                                                        onClick={() => removeUserTarget(index)}
                                                        className="text-gray-400 hover:text-red-600 transition-colors"
                                                    >
                                                        <Trash2 className="h-4 w-4" />
                                                    </button>
                                                )}
                                            </div>

                                            <div className="space-y-3">
                                                <div>
                                                    <input
                                                        type="text"
                                                        value={target.user_id}
                                                        onChange={(e) => updateUserTarget(index, 'user_id', sanitizeInput(e.target.value))}
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                                        placeholder="User ID (e.g. U12345)"
                                                    />
                                                </div>
                                                <div>
                                                    <textarea
                                                        value={getJsonText(index)}
                                                        onChange={(e) => validateAndUpdateJson(index, e.target.value)}
                                                        rows={2}
                                                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 text-sm font-mono ${jsonValidationErrors[index]
                                                            ? 'border-red-500 focus:ring-red-500'
                                                            : 'border-gray-300 focus:ring-blue-500'
                                                            }`}
                                                        placeholder='Placeholders JSON: {"name": "John", "amount": "500"}'
                                                    />
                                                    {jsonValidationErrors[index] && (
                                                        <p className="text-red-600 text-xs mt-1">{jsonValidationErrors[index]}</p>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                    {formErrors.user_targets && <p className="text-red-600 text-sm mt-1">{formErrors.user_targets}</p>}
                                </div>
                            )}
                        </section>

                        {/* Section 3: Nudge Configuration */}
                        <section className="space-y-4">
                            <h3 className="text-sm font-semibold text-gray-700">2. Configure your nudge</h3>

                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                    Nudge Name
                                </label>
                                <input
                                    type="text"
                                    value={nudgeForm.nudge_name}
                                    onChange={(e) => setNudgeForm({ ...nudgeForm, nudge_name: sanitizeInput(e.target.value) })}
                                    className="w-full px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 placeholder-gray-700 nudge-input-bordered"
                                    placeholder="Ex: Test Campaign"
                                />
                                {formErrors.nudge_name && <p className="text-red-600 text-xs mt-1">{formErrors.nudge_name}</p>}
                            </div>

                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                    Message Template
                                </label>
                                <textarea
                                    value={nudgeForm.message_template}
                                    onChange={(e) => setNudgeForm({ ...nudgeForm, message_template: sanitizeInput(e.target.value) })}
                                    rows={3}
                                    className="w-full px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 placeholder-gray-400 resize-none nudge-input-bordered"
                                    placeholder="Ex: Hi {user_name}, invest your {salary} in mutual funds"
                                />
                                {formErrors.message_template && <p className="text-red-600 text-xs mt-1">{formErrors.message_template}</p>}
                            </div>

                            <div className="flex items-start gap-4">
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                        Notification Type
                                    </label>

                                    <div className="relative w-72">
                                        <select
                                            value={nudgeForm.notification_type}
                                            onChange={(e) =>
                                                setNudgeForm({
                                                    ...nudgeForm,
                                                    notification_type: e.target.value as NotificationType,
                                                })
                                            }
                                            className="w-full appearance-none bg-white rounded-xl shadow-sm py-3 text-gray-900 text-sm cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 nudge-input-bordered nudge-select-centered"
                                        >
                                            <option className="px-4 py-3" value="ON_DEMAND">On Demand</option>
                                            <option className="px-4 py-3" value="SCHEDULED">Scheduled</option>
                                            <option className="px-4 py-3" value="RECURRING">Recurring</option>
                                        </select>


                                        {/* Arrow box */}
                                        <div className="absolute inset-y-0 right-0 w-10 flex items-center justify-center rounded-r-xl pointer-events-none bg-white border-l border-[#DBEAFE]">
                                            <ChevronDown className="w-5 h-5 text-gray-700" />
                                        </div>
                                    </div>
                                </div>

                                <div className="flex-1">
                                    {/* TEXT NEXT TO THE FIELD */}
                                    {nudgeForm.notification_type === "ON_DEMAND" && (
                                        <div className="pt-7">
                                            <p className="text-gray-700 text-sm">
                                                Trigger manually from the dashboard.
                                            </p>
                                        </div>
                                    )}

                                    {/* SHOW THE INPUT SECTIONS */}
                                    {nudgeForm.notification_type === 'SCHEDULED' && (
                                        <div>
                                            <p className="text-gray-700 text-sm mb-2">
                                                Sends at the scheduled date and time.
                                            </p>
                                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                                Schedule Time
                                            </label>

                                            <div className="flex items-center gap-4">
                                                {/* Time input */}
                                                <div className="flex-shrink-0">
                                                    <input
                                                        type="time"
                                                        value={nudgeForm.scheduled_date_time?.split("T")[1]?.slice(0, 5) || ""}
                                                        onChange={(e) => {
                                                            const time = e.target.value;
                                                            const currentDate = nudgeForm.scheduled_date_time?.split("T")[0] || "";
                                                            const newDateTime = `${currentDate}T${time}`;
                                                            setNudgeForm({ ...nudgeForm, scheduled_date_time: newDateTime });
                                                        }}
                                                        className="rounded-lg px-3 py-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-36 nudge-input-bordered"
                                                    />
                                                </div>

                                                {/* Date input */}
                                                <div className="flex-shrink-0">
                                                    <input
                                                        type="date"
                                                        value={nudgeForm.scheduled_date_time?.split("T")[0] || ""}
                                                        onChange={(e) => {
                                                            const selectedDate = e.target.value;
                                                            const currentTime = nudgeForm.scheduled_date_time?.split("T")[1] || "00:00";
                                                            const newDateTime = `${selectedDate}T${currentTime}`;
                                                            setNudgeForm({ ...nudgeForm, scheduled_date_time: newDateTime });
                                                        }}
                                                        className="rounded-lg px-3 py-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-44 nudge-input-bordered"
                                                    />
                                                </div>
                                            </div>

                                            {formErrors.scheduled_date_time && (
                                                <p className="text-red-600 text-sm mt-1">{formErrors.scheduled_date_time}</p>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {nudgeForm.notification_type === 'RECURRING' && (
                                <div>
                                    <p className="text-gray-700 text-sm mb-2">
                                        Repeats on a cron schedule.
                                    </p>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                        Cron Expression
                                    </label>
                                    <input
                                        type="text"
                                        value={nudgeForm.cron_expression}
                                        onChange={(e) => setNudgeForm({ ...nudgeForm, cron_expression: e.target.value })}
                                        className="w-full px-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm nudge-input-bordered"
                                        placeholder="0 9 * * MON"
                                    />
                                    {formErrors.cron_expression && <p className="text-red-600 text-sm mt-1">{formErrors.cron_expression}</p>}
                                    <p className="text-xs text-gray-400 mt-1">e.g., "0 9 * * MON" for every Monday at 9 AM</p>
                                </div>
                            )}
                        </section>
                    </div>

                    <div className="p-4 border-t border-gray-200 sticky rounded-lg bottom-0" style={{ backgroundColor: '#ffffff' }}>
                        <button
                            onClick={onCreateNudge}
                            disabled={isCreating}
                            className="nudge-action-btn w-full font-semibold py-3 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
                        >
                            {isCreating ? (
                                <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                    Creating Nudge...
                                </>
                            ) : (
                                'Create Nudge'
                            )}
                        </button>
                    </div>
                </DrawerContent>
            </Drawer>
        </>
    );
};
